import supabase from "./supabase";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { useUiLangStore } from "../stores/uiLang";

interface Student {
  firstName: string;
  lastName?: string;
  email?: string;
}

interface ImportStudentsParams {
  students: Student[];
  classId: string;
}

export async function importStudents({
  students,
  classId,
}: ImportStudentsParams): Promise<{
  message?: string;
  importedEmails: string[];
  newImportedStudents: Array<{ firstName: string; lastName: string; email: string; temporaryPassword: string }>;
  failedEmails: { email: string; reason: string }[];
  error: string | null;
}> {
  const { data, error } = await supabase.functions.invoke("importStudents", {
    body: { students, classId },
  });

  if (error && error instanceof FunctionsHttpError) {
    const errorMessage = await error.context.json();
    return {
      importedEmails: [],
      newImportedStudents: [],
      failedEmails: [],
      error: errorMessage.error,
    };
  }

  // The function returns snake_case (imported / new_students / failed) and camelCase aliases.
  const imported: string[] = data?.importedEmails ?? data?.imported ?? [];
  const created: any[] = data?.newImportedStudents ?? data?.new_students ?? [];
  const failed = data?.failedEmails ?? data?.failed ?? [];
  // Students without an email have no password to hand out: they use a picture PIN.
  const withEmail = created.filter((s) => s.email);
  const kids = created.length - withEmail.length;
  const es = useUiLangStore.getState().lang === "es";
  const kidNote = !kids
    ? ""
    : es
      ? kids === 1
        ? ` 1 estudiante sin correo electrónico puede entrar con el código de clase y un PIN de dibujos. Usa "Imprimir tarjetas" en la clase.`
        : ` ${kids} estudiantes sin correo electrónico pueden entrar con el código de clase y un PIN de dibujos. Usa "Imprimir tarjetas" en la clase.`
      : ` ${kids} student${kids === 1 ? "" : "s"} without an email can sign in with the class code and a picture PIN. Use "Print login cards" on the class.`;
  // No-email rows that match a kid already in this class (first name + last initial) are skipped, not duplicated.
  const skipped: { name: string }[] = Array.isArray(data?.skipped) ? data.skipped : [];
  const names = skipped.map((s) => s.name).join(", ");
  const skipNote = !skipped.length
    ? ""
    : es
      ? ` ${skipped.length} ${skipped.length === 1 ? "estudiante sin correo ya estaba" : "estudiantes sin correo ya estaban"} en esta clase y no se agregaron otra vez: ${names}.`
      : ` ${skipped.length} student${skipped.length === 1 ? "" : "s"} without an email ${skipped.length === 1 ? "was" : "were"} already in this class and ${skipped.length === 1 ? "was" : "were"} not added again: ${names}.`;

  return {
    message: data?.message ? data.message + kidNote + skipNote : (kidNote + skipNote).trim() || undefined,
    importedEmails: imported,
    newImportedStudents: withEmail,
    failedEmails: failed,
    error: null,
  };
}
